/**
 * Query keys centralizadas para TanStack Query.
 * Garante type-safety e unicidade das chaves de cache.
 *
 * Valida: Requisitos 1.7
 */

// === Filter interfaces ===

export interface ExecutionFilters {
  status?: string
  marketplace?: string
  agent_name?: string
  since?: string
  sort_by?: string
}

export interface ProductFilters {
  marketplace?: string
  title?: string
  category_id?: string
  execution_id?: number
  sort_field?: string
  sort_direction?: string
}

export interface DistributionFilters {
  status?: string
  page?: number
  limit?: number
}

export interface ErrorFilters {
  agent_name?: string
  since?: string
}

export interface ObservabilityErrorFilters {
  agent_name?: string
  since?: string
}

// === Query Keys ===

export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  executions: {
    all: ['executions'] as const,
    list: (params: ExecutionFilters) => ['executions', 'list', params] as const,
    detail: (id: number) => ['executions', 'detail', id] as const,
  },
  products: {
    all: ['products'] as const,
    list: (params: ProductFilters) => ['products', 'list', params] as const,
  },
  categories: {
    all: ['categories'] as const,
    list: () => ['categories', 'list'] as const,
    search: (q: string, marketplace: string) => ['categories', 'search', q, marketplace] as const,
    top: (marketplace: string) => ['categories', 'top', marketplace] as const,
    details: (id: string) => ['categories', 'details', id] as const,
  },
  distribution: {
    pending: ['distribution', 'pending'] as const,
    scheduled: ['distribution', 'scheduled'] as const,
    sent: (params: DistributionFilters) => ['distribution', 'sent', params] as const,
    history: (params?: Record<string, unknown>) => ['distribution', 'history', params ?? {}] as const,
  },
  dashboard: {
    kpis: (since?: string) => ['dashboard', 'kpis', since] as const,
  },
  metrics: {
    byAgent: (since?: string) => ['metrics', 'by-agent', since] as const,
    business: (since?: string) => ['metrics', 'business', since] as const,
    errors: (params: ErrorFilters) => ['metrics', 'errors', params] as const,
    executionsByDay: (since?: string) => ['metrics', 'executions-by-day', since] as const,
    operationalSummary: (since?: string) => ['metrics', 'operational-summary', since] as const,
  },
  observability: {
    agentNames: (since?: string) => ['observability', 'agent-names', since] as const,
    errors: (params: ObservabilityErrorFilters) => ['observability', 'errors', params] as const,
    llmUsage: (since?: string) => ['observability', 'llm-usage', since] as const,
  },
  agents: {
    all: ['agents'] as const,
  },
  collectors: {
    all: ['collectors'] as const,
  },
  whatsapp: {
    status: () => ['whatsapp', 'status'] as const,
    groups: () => ['whatsapp', 'groups'] as const,
    destinations: () => ['whatsapp', 'destinations'] as const,
  },
  billing: {
    me: () => ['billing', 'me'] as const,
    plans: () => ['billing', 'plans'] as const,
  },
  recommendations: {
    all: ['recommendations'] as const,
    collectors: () => ['recommendations', 'collectors'] as const,
    categories: (collector: string, parentId: string | null) =>
      ['recommendations', collector, 'categories', parentId] as const,
    ranking: (collector: string, categoryId: string) => ['recommendations', collector, 'ranking', categoryId] as const,
    trends: (collector: string, categoryId: string | null) =>
      ['recommendations', collector, 'trends', categoryId] as const,
  },
  productSearch: {
    // Prefixo único para `invalidateQueries({ queryKey: all, refetchType: 'none' })`:
    // "Já coletado" é derivado do banco a cada leitura, mas a página fica 1 h no
    // cache — quem coleta ou exclui produto marca a busca como velha sem refazê-la.
    all: ['product-search'] as const,
    results: (collector: string, q: string) => ['product-search', collector, q] as const,
  },
} as const
